import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngxs/store';
import {
  WorkMontageplanDayDto,
  WorkMontageplanUsersTeamPartial,
  WorkMontageplanV2Service,
  WorkTimerecordtypeNameEnum,
} from '@work-frontend-monorepo/work-rest-api';
import { AvatarComponent } from '../avatar/avatar.component';
import { FormatDatePipe } from '../../pipes/format-date.pipe';
import { GetAvatarStatusByTimerecordtypePipe } from '../../pipes/get-avatar-status-by-timerecordtype.pipe';
import { GetInitialsPipe } from '../../pipes/get-initials.pipe';
import { GetTimerecordtypeIconNamePipe } from '../../pipes/get-timerecordtype-icon-name.pipe';
import { IconComponent } from '../icon/icon.component';
import { TooltipDirective } from '../../directives/tooltip/tooltip.directive';
import { lastValueFrom } from 'rxjs';
import dayjs from 'dayjs';
import { TranslatePipe } from '@ngx-translate/core';
import { ExceptionHandlingService } from '../../services/exception-handling/exception-handling.service';
import { PrependServerToFileSrcPipe } from '../../pipes/prepend-server-to-file-src.pipe';
import { LowerCasePipe } from '@angular/common';

@Component({
  selector: 'work-ui-montageplan-personal-planning-overview',
  templateUrl: './montageplan-personal-planning-overview.component.html',
  styleUrls: ['./montageplan-personal-planning-overview.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    AvatarComponent,
    GetInitialsPipe,
    PrependServerToFileSrcPipe,
    TooltipDirective,
    IconComponent,
    GetTimerecordtypeIconNamePipe,
    GetAvatarStatusByTimerecordtypePipe,
    FormatDatePipe,
    TranslatePipe,
    LowerCasePipe,
  ],
})
export class MontageplanPersonalPlanningOverviewComponent {
  private readonly store = inject(Store);
  private readonly exceptionHandlingService = inject(ExceptionHandlingService);
  private readonly workMontageplanV2Service = inject(WorkMontageplanV2Service);

  // Inputs
  public readonly userId = input.required<number>();
  public readonly fromDate = input<string>(dayjs().format('YYYY-MM-DD'));
  public readonly toDate = input<string>(dayjs().format('YYYY-MM-DD'));
  public readonly hideSundaysAndEmptySaturdays = input<boolean>(false);

  // Outputs
  public readonly openProjectDetails = output<number>();
  public readonly openUserDetails = output<number>();

  protected readonly workMontageplanDayDtos = signal<WorkMontageplanDayDto[]>([]);
  protected readonly workMontageplanUsersTeamPartial = signal<WorkMontageplanUsersTeamPartial[]>([]);

  protected readonly isToday = computed(() => {
    return dayjs(this.fromDate()).isSame(dayjs(), 'day') && dayjs(this.toDate()).isSame(dayjs(), 'day');
  });
  protected readonly filteredDayDtos = computed(() => {
    const dtos = this.workMontageplanDayDtos();
    if (!this.hideSundaysAndEmptySaturdays()) return dtos;
    return dtos.filter((dto) => {
      const dow = dayjs(dto.date).day(); // 0 = Sonntag, 6 = Samstag
      if (dow === 0) return false; // Sonntage nie anzeigen
      if (dow === 6) {
        // Samstag: nur wenn Mitarbeiter einem Team zugewiesen ist UND Projekte/Tasks vorhanden sind
        return dto.teams.length > 0 && dto.teams.some((t) => (t.blocks?.length ?? 0) > 0 || (t.tasks?.length ?? 0) > 0);
      }
      return true;
    });
  });

  private readonly loadDayDataOnUserChangeEffect = effect(() => {
    const userId = this.userId();
    void this.loadMontageplanDayData(userId);
  });

  private readonly loadTeamDataOnDayDataChangeEffect = effect(() => {
    const teamIds = this.workMontageplanDayDtos()
      .map((dto) => dto.teams)
      .flat()
      .map((team) => team.teamId)
      .flat();
    if (teamIds.length === 0) {
      return;
    }
    void this.loadMontageplanTeamData(teamIds[0]);
  });

  protected readonly WorkTimerecordtypeNameEnum = WorkTimerecordtypeNameEnum;
  protected readonly dayjs = dayjs;

  private async loadMontageplanDayData(userId: number) {
    try {
      const workMontageplanDayDtos = await lastValueFrom(
        this.workMontageplanV2Service.montageplanV2ControllerGetMontageplanBlocksByUserIdAndDaterange({
          userId: userId,
          fromDate: this.fromDate(),
          toDate: this.toDate(),
        }),
      );
      this.workMontageplanDayDtos.set(workMontageplanDayDtos);
    } catch (loadMontageplanDataException) {
      this.exceptionHandlingService.handleExceptionWithAlert('loadMontageplanDataException', loadMontageplanDataException);
    }
  }

  private async loadMontageplanTeamData(teamId: number) {
    try {
      const montageplanUsersDto = await lastValueFrom(
        this.workMontageplanV2Service.montageplanV2ControllerGetMontageplanUsersByTeamIdAndDaterange({
          fromDate: this.fromDate(),
          toDate: this.toDate(),
          teamIds: [`${teamId}`],
        }),
      );
      this.workMontageplanUsersTeamPartial.set(montageplanUsersDto.teams[teamId]);
    } catch (dataNotLoadedException) {
      this.exceptionHandlingService.handleExceptionWithAlert('dataNotLoadedException', dataNotLoadedException);
    }
  }
}
